import React, { forwardRef, useImperativeHandle, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Form } from 'antd';

// Shared UI Library Components
import {
  CustomButton,
  CustomTextArea,
  DiscardModal,
  InputReadOnly
} from '@ppmcore/seven-ppm-core-shared-components-react';

// Store entities
import { updateCompanyAbout } from '../../../../../../store/company/company.thunks';

// Styles
import './about.scss';

// Models
import { ICompanyProfile } from '../../../../../../interfaces/company.interfaces';

export type TAboutRef = {
  isHasChanges: () => boolean;
  resetState: () => void;
};

type TAboutProps = {
  company: ICompanyProfile;
};

export const About = forwardRef<TAboutRef, TAboutProps>((
    { company }: TAboutProps, ref
  ) => {
    const [form] = Form.useForm<{ about: string }>();

    const [discardOpen, setDiscardOpen] = useState<boolean>(false);
    const [hasChanges, setHasChanges] = useState<boolean>(false);
    const [editMode, setEditMode] = useState<boolean>(false);
    const [savingChanges, setSavingChanges] = useState<boolean>(false);

    const dispatch = useDispatch<any>();

    const toggleEditMode = (): void => {
      setEditMode(!editMode);
    }

    const toggleDiscardModal = (): void => {
      setDiscardOpen(!discardOpen);
    }

    const onCancelChangesHandler = (): void => {
      if (hasChanges) {
        toggleDiscardModal();
        return;
      }
      toggleEditMode();
    }

    const onDiscardChangesHandler = (state: boolean): void => {
      if (state) {
        form.resetFields();
        setHasChanges(false);
        toggleEditMode();
        toggleDiscardModal();
        return;
      }
      toggleDiscardModal();
    }

    const onValuesChange = (_: { [key: string]: string | string[] }, { about }: {
      about: string,
    }): void => {
      const isHasChanges = company?.about !== about;
      setHasChanges(isHasChanges);
    }

    const saveChanges = async (): Promise<void> => {
      const { about } = form.getFieldsValue();
      setSavingChanges(true);
      const { payload } = await dispatch(updateCompanyAbout(about));
      setSavingChanges(false);
      if (!payload) return;
      setEditMode(false);
      setHasChanges(false);
      form.resetFields();
    }

    useImperativeHandle(ref, () => ({
      isHasChanges(): boolean {
        return hasChanges;
      },
      resetState(): void {
        form.setFieldsValue({
          about: company?.about ?? '',
        });
        setHasChanges(false);
        setEditMode(false);
        setSavingChanges(false);
      }
    }));

    return (
      <div className="about">
        <DiscardModal
          open={ discardOpen }
          onCancel={ onDiscardChangesHandler }
          title={ 'Are you sure you want to discard changes?' }
        />
        <div className="about--label business--label">
          <div className="label-title">
            About
          </div>
          <div className="label-actions">
            { !editMode &&
              <CustomButton type={ 'text' }
                            text={ company?.about ? 'Edit' : 'Add' }
                            size={ 'small' }
                            onClick={ toggleEditMode }/>
            }

            { editMode && <>
              { hasChanges && <CustomButton type={ 'primary' }
                              text={ 'Save' }
                              size={ 'small' }
                              loading={ savingChanges }
                              onClick={ saveChanges }/> }
              <CustomButton type={ 'text' }
                            text={ 'Cancel' }
                            size={ 'small' }
                            disabled={ savingChanges }
                            onClick={ onCancelChangesHandler }/>
            </> }
          </div>
        </div>
        <Form className="about--body business--body-items" form={ form } onValuesChange={ onValuesChange }>
          <div className="body-item">
            { (!editMode && company?.about) &&
              <InputReadOnly label={ 'About' } value={ company?.about }/> }

            { editMode &&
              <Form.Item name={ 'about' } initialValue={ company?.about }>
                <CustomTextArea
                  placeholder={ 'About' }
                  maxLength={ 500 }
                  autoSize={ {
                    minRows: 2,
                    maxRows: 20
                  } }
                  showCount={ true }
                />
              </Form.Item> }
          </div>
        </Form>
      </div>
    );
  }
)
